import { call } from './api.js';

/* ══════════════════════════════════════════════════════════════════════════
 * SENDING A PHOTOGRAPH FROM A PHONE
 *
 * ⚠️ NINETEEN SCREENS HAD A FILE CONTROL AND NOTHING BEHIND IT. The Feeds screen
 * has said "＋ Upload a photo or video" since it was built. This is the piece
 * that makes the file control mean something.
 *
 * ── ⚠️ THE PHOTO IS SHRUNK BEFORE IT IS SENT, AND THAT IS THE WHOLE BILL ────
 *
 * A modern phone camera produces a 12-megapixel frame of three to six
 * megabytes. A feed tile is a few hundred pixels across on a phone screen.
 * Sending the full frame means paying to store ten times what is needed,
 * waiting ten times as long on a restaurant's wifi, and then making every
 * Eleater download it to look at a thumbnail.
 *
 * Shrinking here turns three megabytes into roughly three hundred kilobytes
 * with no visible difference on any screen Eleat runs on. Al asked what storage
 * costs at 50, 100 and 500 restaurants; this function is the reason those
 * numbers are single digits rather than an unpleasant surprise.
 *
 * ── ⚠️ VIDEO IS NOT TOUCHED, DELIBERATELY ───────────────────────────────────
 *
 * A browser cannot re-encode video without a large amount of machinery, and
 * building that for launch is not a small change. Video is sent as the phone
 * recorded it, capped at a size the server enforces in the signature itself.
 *
 * ⚠️ AND THERE IS A KNOWN RISK HERE, RAISED NOW RATHER THAN AT SIGN-OFF
 * (RULE 7). iPhones record in HEVC, and Safari used to convert that to the
 * universally-playable format on upload — it stopped doing so in iOS 13.6.1, so
 * a web upload now receives the original. HEVC plays on Apple devices and on
 * much but not all of Android. **This must be tested with a real video from a
 * real phone before any restaurant relies on it.** If it fails, the answer is
 * Cloudflare Stream, which converts every upload automatically and lives in the
 * same account as the storage — not a second company and not a rebuild.
 *
 * ── ⚠️ THE FILE NEVER GOES THROUGH ELEAT'S SERVER ───────────────────────────
 *
 * Eleat signs permission; the phone sends the bytes straight to storage. See
 * `4. Backend/src/lib/storage.js`. What comes back from `/uploads/sign` is good
 * for ONE file, of exactly the declared size and type, for ten minutes.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ 1600px IS A DECISION ABOUT THE LARGEST SCREEN ELEAT RUNS ON, not a round
 * number. The Restaurant Admin About page shows a cover photo across a desktop
 * window; 1600 covers that at a normal pixel density and covers every phone
 * twice over. Going higher costs storage for pixels nobody sees. */
const MAX_EDGE = 1600;
const QUALITY = 0.82;

export const IS_VIDEO = (type) => String(type || '').startsWith('video/');

/**
 * Shrink a photo in the browser. Returns a Blob, or the original file
 * untouched if anything at all goes wrong.
 *
 * ⚠️ IT NEVER THROWS, AND IT NEVER BLOCKS THE UPLOAD. A photo that could not be
 * shrunk is still a photo the restaurant wants to post. The size cap on the
 * server is the control; this is the optimisation. Confusing the two would mean
 * an Eleater on an unusual browser simply cannot post at all.
 */
export async function shrink(file) {
  if (IS_VIDEO(file.type)) return file;
  try {
    /* ⚠️ createImageBitmap, NOT an <img> with an object URL. It decodes off the
       main thread, so a large photo does not freeze the screen mid-post, and it
       honours the phone's rotation flag — without which portrait photos from an
       iPhone arrive on their side. */
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));

    /* already small enough — re-encoding would only lose quality */
    if (scale === 1 && file.size < 900 * 1024) { bmp.close?.(); return file; }

    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
    bmp.close?.();

    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', QUALITY));
    /* ⚠️ IF SHRINKING MADE IT BIGGER, KEEP THE ORIGINAL. That happens with an
       already-optimised photo, and shipping the larger one would be the
       opposite of the point. */
    if (!blob || blob.size >= file.size) return file;
    return blob;
  } catch {
    return file;
  }
}

/* ⚠️ THREE AT A TIME, NOT ALL OF THEM AT ONCE. Al, 22 September 2026: *"what if
 * they have multiple uploads at a time. like for example 5 pics in a set"* —
 * and the locked Feeds screen has always said "Select more than one at a time".
 *
 * Twenty photos fired simultaneously over a restaurant's wifi do not go twenty
 * times faster; they compete for the same pipe, every progress bar crawls
 * together, and a phone browser will queue most of them anyway. Three keeps the
 * connection busy and the bars honest. */
const AT_A_TIME = 3;

/** Send the bytes through a door that has already been signed. */
function put(url, contentType, body, onProgress) {
  /* ⚠️ XMLHttpRequest RATHER THAN fetch, FOR ONE REASON: progress. `fetch`
     cannot report how far an upload has got, and a restaurant sending a 60MB
     video over a slow connection with no bar assumes it has frozen and starts
     again — which is how one upload becomes four. */
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error('That photo could not be sent. Try again.')));
    xhr.onerror = () => reject(new Error('That photo could not be sent. Try again.'));
    xhr.send(body);
  });
}

/**
 * Send a whole set. Returns one { key, media_type, public_url } per file, in
 * the order they were given.
 *
 * ⚠️ ONE REQUEST SIGNS THE WHOLE SET. Five photos used to mean five separate
 * trips to Eleat before a single byte moved, and forty — a restaurant filling
 * their Feeds for the first time, which is exactly when they will do it — would
 * have hit the rate limit partway through and died in the middle of the batch.
 *
 * ⚠️ AND THE SERVER CHECKS EVERY FILE BEFORE IT HANDS OUT ANY DOOR, so a set of
 * five where the third is too large refuses the whole set and says which one is
 * the problem. Two uploaded and three not, with no explanation, is worse than a
 * clean refusal.
 *
 * @param files         an array of File, straight from a `multiple` input
 * @param restaurantId  present for a Feeds post, absent for a profile photo
 * @param onProgress    (done, total, fraction) — for "3 of 5" and a bar
 */
export async function uploadMany(files, { restaurantId, onProgress } = {}) {
  const list = Array.from(files || []);
  if (list.length === 0) return [];

  /* ⚠️ SHRINK FIRST, THEN SIGN. The signature covers the exact byte count and
     the exact type, so the server has to be told about the file that will
     actually be sent — not the camera frame it started as. Signing before
     shrinking would make every signature wrong, and the error the provider
     returns for that blames the credentials, which sends somebody looking at
     the wrong dashboard for an hour. */
  const bodies = await Promise.all(list.map(shrink));

  const { uploads } = await call('/uploads/sign', {
    method: 'POST', auth: true,
    body: {
      files: bodies.map((b, i) => ({
        content_type: b.type || list[i].type,
        content_length: b.size,
      })),
      ...(restaurantId ? { restaurant_id: restaurantId } : {}),
    },
  });

  const out = new Array(list.length);
  const share = new Array(list.length).fill(0);
  let done = 0;
  const tell = () => {
    if (!onProgress) return;
    const frac = share.reduce((a, b) => a + b, 0) / list.length;
    onProgress(done, list.length, frac);
  };

  let next = 0;
  async function worker() {
    for (;;) {
      const i = next++;
      if (i >= list.length) return;
      const u = uploads[i];
      await put(u.url, u.content_type, bodies[i], (f) => { share[i] = f; tell(); });
      share[i] = 1; done += 1; tell();
      out[i] = { key: u.key, media_type: u.media_type, public_url: u.public_url };
    }
  }
  /* ⚠️ ONE FAILURE FAILS THE SET, deliberately — `Promise.all` rejects on the
     first. A restaurant who selected five photos wants five photos posted, and
     silently posting four is the half-done job again. They retry the set. */
  await Promise.all(Array.from({ length: Math.min(AT_A_TIME, list.length) }, worker));
  return out;
}

/**
 * Send one file. Returns { key, media_type, public_url }.
 *
 * ⚠️ IT IS `uploadMany` WITH A LIST OF ONE, not a second implementation. One
 * file and several files taking different paths through Eleat is two copies of
 * one truth, and the day they disagree the single-photo case works and the set
 * does not — or worse, the other way round, which nobody notices until a
 * restaurant posts a set.
 */
export async function upload(file, opts = {}) {
  const { onProgress, ...rest } = opts;
  const [one] = await uploadMany([file], {
    ...rest,
    /* the single-file caller wants a plain 0 → 1, not a count */
    onProgress: onProgress ? (_d, _t, frac) => onProgress(frac) : undefined,
  });
  return one;
}

/**
 * Is uploading switched on at all?
 *
 * ⚠️ ASKED, NEVER ASSUMED. Storage is configuration, and a screen that offers a file
 * control when there is nowhere to put the file produces the worst outcome in this
 * project: an action that appears to succeed and stores nothing.
 */
export async function uploadsEnabled() {
  try {
    const c = await call('/uploads/config', { auth: true });
    return !!c.enabled;
  } catch {
    return false;
  }
}
